import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { ApiError } from '@formosa/shared/api/createClient';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { subscribeFuturesRoundOdds } from '@formosa/shared/socket/futuresSocket';
import {
  CONFIG_ODDS_METHOD_OPTIONS,
  CONFIG_OVERRIDE_OPTIONS,
  formatOddsPercent,
  isRoundConfigurable,
} from './futuresRoundConfigOptions';

const SINGLE_OVERRIDE_OPTIONS = CONFIG_OVERRIDE_OPTIONS;
const ODDS_METHOD_OPTIONS = CONFIG_ODDS_METHOD_OPTIONS;

export default function FuturesRoundConfigModal({ roundCode, open, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [liveOdds, setLiveOdds] = useState({ goLong: null, goShort: null });
  const [form, setForm] = useState({
    oddsMethod: 'RANDOM_RATE',
    overrideMethod: 'GLOBAL_CONTROL',
  });

  const title = useMemo(
    () => `週期配置${roundCode ? ` (${roundCode})` : ''}`,
    [roundCode]
  );

  const editable = detail ? (detail.configurable ?? isRoundConfigurable(detail.state)) : true;

  useEffect(() => {
    if (!open || !roundCode) {
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    adminApi
      .futuresRoundConfigDetail(roundCode)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setDetail(data);
        setForm({
          oddsMethod: data?.oddsMethod === 'DEFAULT' ? data?.effectiveOddsMethod || 'RANDOM_RATE' : data?.oddsMethod || 'RANDOM_RATE',
          overrideMethod:
            data?.overrideMethod === 'DEFAULT'
              ? data?.effectiveOverrideMethod === 'DO_NOTHING'
                ? 'GLOBAL_CONTROL'
                : data?.effectiveOverrideMethod || 'GLOBAL_CONTROL'
              : data?.overrideMethod || 'GLOBAL_CONTROL',
        });
        setLiveOdds({
          goLong: data?.goLongOddsPercent,
          goShort: data?.goShortOddsPercent,
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : '載入失敗');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, roundCode]);

  useEffect(() => {
    if (!open || !roundCode) {
      return undefined;
    }
    return subscribeFuturesRoundOdds(roundCode, {
      onUpdate: (data) => {
        setLiveOdds({
          goLong: data?.goLongOdds ?? null,
          goShort: data?.goShortOdds ?? null,
        });
      },
    });
  }, [open, roundCode]);

  function handleReset() {
    if (!detail) {
      return;
    }
    setForm({
      oddsMethod:
        detail.oddsMethod === 'DEFAULT'
          ? detail.effectiveOddsMethod || 'RANDOM_RATE'
          : detail.oddsMethod || 'RANDOM_RATE',
      overrideMethod:
        detail.overrideMethod === 'DEFAULT'
          ? detail.effectiveOverrideMethod === 'DO_NOTHING'
            ? 'GLOBAL_CONTROL'
            : detail.effectiveOverrideMethod || 'GLOBAL_CONTROL'
          : detail.overrideMethod || 'GLOBAL_CONTROL',
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!roundCode || !editable) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await adminApi.configFuturesRounds({
        roundCodes: [roundCode],
        oddsMethod: form.oddsMethod,
        overrideMethod: form.overrideMethod,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '提交失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>{title}</h3>
          <button type="button" className="admin-modal__close" onClick={onClose} aria-label="關閉">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body admin-modal__body--form">
            {loading && <p>載入中…</p>}
            {error && <div className="alert alert-error">{error}</div>}

            {detail && !loading && (
              <>
                {!editable && (
                  <div className="alert alert-error">
                    僅「進行中」或「待開獎」狀態的週期可修改配置
                  </div>
                )}

                <div className="config-detail-grid">
                  <div className="form-field">
                    <label>週期 ID</label>
                    <div className="readonly-field mono-cell">{detail.roundCode}</div>
                  </div>
                  <div className="form-field">
                    <label>合約時間</label>
                    <div className="readonly-field">{formatDateTime(detail.endTime)}</div>
                  </div>
                  <div className="form-field">
                    <label>產品名稱_週期</label>
                    <div className="readonly-field">{detail.productNamePeriod || '—'}</div>
                  </div>
                  <div className="form-field">
                    <label>週期狀態</label>
                    <div className="readonly-field">{detail.stateLabel || '—'}</div>
                  </div>
                </div>

                <fieldset className="config-fieldset">
                  <legend>賠率設置 *</legend>
                  {ODDS_METHOD_OPTIONS.map((item) => (
                    <label key={item.value} className="radio-inline">
                      <input
                        type="radio"
                        name="oddsMethod"
                        value={item.value}
                        checked={form.oddsMethod === item.value}
                        disabled={!editable}
                        onChange={() => setForm((prev) => ({ ...prev, oddsMethod: item.value }))}
                      />
                      {item.label}
                    </label>
                  ))}
                </fieldset>

                <div className="config-odds-row">
                  <div className="form-field">
                    <label>做多盈利率</label>
                    <div className="readonly-field odds-highlight">
                      {formatOddsPercent(liveOdds.goLong)}
                    </div>
                  </div>
                  <div className="form-field">
                    <label>做空盈利率</label>
                    <div className="readonly-field odds-highlight">
                      {formatOddsPercent(liveOdds.goShort)}
                    </div>
                  </div>
                </div>

                <fieldset className="config-fieldset">
                  <legend>場控設置 *</legend>
                  {SINGLE_OVERRIDE_OPTIONS.map((item) => (
                    <label key={item.value} className="radio-inline">
                      <input
                        type="radio"
                        name="overrideMethod"
                        value={item.value}
                        checked={form.overrideMethod === item.value}
                        disabled={!editable}
                        onChange={() =>
                          setForm((prev) => ({ ...prev, overrideMethod: item.value }))
                        }
                      />
                      {item.label}
                    </label>
                  ))}
                </fieldset>
              </>
            )}
          </div>

          <div className="admin-modal__footer admin-modal__footer--center">
            <button type="submit" className="btn btn-success" disabled={submitting || loading || !editable}>
              {submitting ? '提交中…' : '提交'}
            </button>
            <button type="button" className="btn btn-warning" onClick={handleReset} disabled={loading}>
              重置
            </button>
            <button type="button" className="btn btn-danger" onClick={onClose}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
